import { Integer, Long, IntegerString, PercentageFloatString, DateString, EmailAddress } from '../../common';

export interface Pagination {
    UsePagination: boolean;
    Start?: number; // Defaults to 0
    Per?: number; // Defaults to 30
    SortColumn: string;
    SortOrder?: 'ASC' | 'DESC'; // Defaults to ASC
}

export interface BaseRequest {
    Pagination: Pagination;
}

export interface ResultList<T> {
    Results: T[];
    TotalRows: number;
    Message: string;
}

export interface BaseResponse<R> {
    Result: R;
    Code: string;
    Message: string;
}

export interface GetAccountRequest {
    ReferenceCode: string;
}

export interface GetAccountResult {
    ID: number;
    ParentID: number;
    Name: string;
    APIKey: string;
    TimeZoneID: null;
    SecretKey: string;
    ReferenceCode: string;
    TreePath: string;
    UsePaymentProcessorAggregatedTransactions: boolean | null;
    TreeLevel: number;
    WebsiteLocationID: number;
    FWClubID: number;
    IsActive: boolean;
    CreateUser: string;
    CreateDate: string;
    UpdateUser: string;
    UpdateDate: string;
}

export type GetAccountResponse = BaseResponse<GetAccountResult>;

export interface GetBusinessesRequest extends BaseRequest {
    BusinessEntityID?: Integer;
    BusinessEntityCode?: string;
    CountryID?: Integer;
    CountryISOAlpha3?: string;
}

export const GetBusinessesResponseColumns = Object.freeze({
    BusinessCode: 'BusinessCode',
    BusinessEntityCode: 'BusinessEntityCode',
    BusinessID: 'BusinessID',
    BusinessName: 'BusinessName',
    CountryISOAlpha3: 'CountryISOAlpha3',
    HasDeclineRecovery: 'HasDeclineRecovery',
    HasMemberEngager: 'HasMemberEngager',
});

export interface GetBusinessesResponseEntry {
    BusinessID: Integer;
    BusinessName: string;
    BusinessCode: string;
    BusinessEntityCode: string;
    CountryISOAlpha3?: string;
    HasDeclineRecovery: boolean;
    HasMemberEngager: boolean;
}

export type GetBusinessesResponse = BaseResponse<ResultList<GetBusinessesResponseEntry>>;

export interface GetClubsRequest extends BaseRequest {
    BusinessID?: Integer;
    BusinessCode?: string;
}

export const GetClubsResponseColumns = Object.freeze({
    BusinessID: 'BusinessID',
    BusinessName: 'BusinessName',
    City: 'City',
    ClubID: 'ClubID',
    ClubName: 'ClubName',
    ClubNumber: 'ClubNumber',
    CountryISOAlpha3: 'CountryISOAlpha3',
    Email: 'Email',
    Latitude: 'Latitude',
    Longitude: 'Longitude',
    Phone: 'Phone',
    PostalCode: 'PostalCode',
    StateISOAlpha2: 'StateISOAlpha2',
    StatusName: 'StatusName',
    Street: 'Street',
    Street2: 'Street2',
});

export interface GetClubsResponseEntry {
    AccountID: number;
    Brand: string;
    BusinessID: Integer;
    BusinessName: string;
    ClubID: number; // Docs say this is a string, returned as a number...
    ClubName: string;
    ClubNumber: string;
    Country: string;
    Street: string;
    Street2: string;
    City: string;
    StateISOAlpha2: string;
    PostalCode: string;
    CountryISOAlpha3: string;
    Latitude: string;
    Longitude: string;
    Email: EmailAddress;
    Phone: string;
    StatusName: string;
    OwnershipEntity: string;
    BillingFrequencies: string[];
}

export type GetClubsResponse = BaseResponse<ResultList<GetClubsResponseEntry>>;

export type MemberStatus = 'Delinquent' | 'Freeze' | 'Inactive' | 'OK' | 'Pending' | 'Suspended' | 'Terminated';
export type ProspectStatus = 'Active' | 'Inactive' | 'New' | 'Signed Membership';

export interface GetCustomersRequest extends BaseRequest {
    ClubNumber?: Integer;
    PersonTypeName?: 'Member' | 'Prospect';
    Statuses?: Array<MemberStatus | ProspectStatus>;
}

export const GetCustomersResponseColumns = Object.freeze({
    Barcode: 'Barcode',
    City: 'City',
    CountryISOAlpha3: 'CountryISOAlpha3',
    CustomerID: 'CustomerID',
    Email: 'Email',
    FirstName: 'FirstName',
    LastName: 'LastName',
    MemberSinceDate: 'MemberSinceDate',
    PersonTypeName: 'PersonTypeName',
    Phone: 'Phone',
    PostalCode: 'PostalCode',
    StateISOAlpha2: 'StateISOAlpha2',
    Status: 'Status',
    Street1: 'Street1',
    Street2: 'Street2',
});

export interface GetCustomersResponseEntry {
    CustomerID: Integer;
    Barcode: string;
    PersonTypeName: string;
    FirstName: string;
    LastName: string;
    Street1: string;
    Street2: string;
    City: string;
    StateISOAlpha2: string;
    CountryISOAlpha3: string;
    PostalCode: string;
    Phone: string;
    Email: string;
    Status: string;
    MemberSinceDate: Date;
}

export type GetCustomersResponse = BaseResponse<ResultList<GetCustomersResponseEntry>>;

export interface GetMembershipPlans extends BaseRequest {
    ClubNumber: Integer;
}

export type Dues = string; // Format of $$.$$

export interface MembershipServicePlanItem {
    ItemID: Integer;
    ItemName: string;
    ItemTypeID: Long;
    ItemCategoryName: string;
    IsTaxInclusive: boolean;
    Price: string;
    TaxRate: string;
    TaxAmount: string;
    TotalAmount: string;
}

export const GetMembershipPlansResponseColumns = Object.freeze({
    BillingFrequencyName: 'BillingFrequencyName',
    ClubID: 'ClubID',
    ClubName: 'ClubName',
    CurrencyISOCode: 'CurrencyISOCode',
    EnrollmentDisplayDescription: 'EnrollmentDisplayDescription',
    EnrollmentDisplayName: 'EnrollmentDisplayName',
    MembershipDues: 'MembershipDues',
    MembershipPlanID: 'MembershipPlanID',
    MembershipPlanName: 'MembershipPlanName',
});

export interface GetMembershipPlansResponseEntry {
    ClubID: Integer;
    ClubName: string;
    MembershipPlanID: Integer;
    MembershipPlanName: string;
    EnrollmentDisplayName: string;
    EnrollmentDisplayDescription: string;
    CurrencyISOCode: string;
    MembershipDues: Dues;
    BillingFrequencyName: string;
    MembershipServicePlanItems: MembershipServicePlanItem[];
}

export type GetMembershipPlansResponse = BaseResponse<ResultList<GetMembershipPlansResponseEntry>>;

export interface GetProspectsReportsRequest {
    FromDateTime?: DateString;
    ToDateTime?: DateString;
    ClubNumber?: IntegerString;
    ReportType?: 'multi_club_summary' | 'single_club';
}

export interface BaseProspectReportEntry {
    Name: string;
    Type: 'company' | 'region' | 'user';
    ReferralsPos: number;
    NewLeads: number;
    AppointmentsBookedSameDay: number;
    AppointmentsBookedForTomorrow: number;
    AppointmentsBookedForFutureDate: number;
    ServiceCalls: number;
    Contacts: number;
    ContactAttempts: number;
    TourToday: number;
    TourShowUp: number;
    TourSale: number;
    TourTotal: number;
    WalkInTotal: number;
    WalkInSale: number;
    PersonalTrainingSold: number;
    MembershipNoContract: number;
    Membership12Month: number;
    Total: number;
    TotalSale: number;
    ReferralsPerSale: number;
    ContactRatio: PercentageFloatString;
    TourShowRatio: PercentageFloatString;
    TourCloseRatio: PercentageFloatString;
    WalkInCloseRatio: PercentageFloatString;
    TotalCloseRatio: PercentageFloatString;
    Membership12MonthRatio: PercentageFloatString;
}

export interface GetProspectsReportsResponseEntryEntityData extends BaseProspectReportEntry {
    ID: number;
    Region: string;
    TotalBooked: number;
}

export interface GetProspectsReportsResponseEntry extends BaseProspectReportEntry {
    Code: string;
    EntityData: GetProspectsReportsResponseEntryEntityData[];
    BookedTotal: number;
}

export interface EntityList<E> {
    Entities: E[];
}

export type GetProspectsReportsResponse = BaseResponse<EntityList<GetProspectsReportsResponseEntry>>;

/*
 * Other endpoints:
 * - GetEnrollmentMemberPricing
 * - EnrollMembership
 * - GetClubPaymentGatewaySetting
 */
